import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircle, Close, Smartphone } from '@mui/icons-material';
import React, { useCallback, useEffect, useState } from 'react';
import { customFetcher } from '../hooks/useFetch';
import { prettyClientName } from '../core/clientNames';

interface IPendingAuth {
  userCode: string;
  ClientName: string;
  Status: 'pending' | 'approved' | 'denied' | 'expired';
  IP?: string | null;
  UserAgent?: string | null;
  Expires_at?: string;
}

type Stage =
  | 'input'
  | 'loading'
  | 'confirm'
  | 'approved'
  | 'denied'
  | 'notFound'
  | 'expired'
  | 'error';

type IProps = {
  open: boolean;
  onClose: () => void;
  /** Wird nach erfolgreicher Freigabe gerufen, damit die Sitzungsliste die neue
   *  Verbindung sofort zeigt. */
  onConnected: () => void;
};

/**
 * Gerät im Sitzungsbereich verbinden: der Benutzer tippt den Freigabe-Code ein,
 * den ihm die DiNo-Mitarbeiter-App (oder ein Plugin) anzeigt, und bestätigt die
 * Verbindung. Fachlich derselbe Device-Flow wie auf der Seite `/authorize` —
 * nur ohne Ortswechsel, damit die neue Sitzung direkt in der Liste erscheint.
 */
export const ConnectDeviceDialog: React.FC<IProps> = ({
  open,
  onClose,
  onConnected,
}) => {
  const [code, setCode] = useState('');
  const [pending, setPending] = useState<IPendingAuth | null>(null);
  const [stage, setStage] = useState<Stage>('input');
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Bei jedem Öffnen frisch starten (sonst steht der alte Code noch da).
  useEffect(() => {
    if (open) {
      setCode('');
      setPending(null);
      setStage('input');
      setBusy(false);
      setErrorMsg(null);
    }
  }, [open]);

  const lookup = useCallback(async () => {
    const cleaned = code.trim().toUpperCase();
    if (!cleaned) return;
    setStage('loading');
    setErrorMsg(null);
    try {
      const data = (await customFetcher({
        endpoint: `/oauth/device/${encodeURIComponent(cleaned)}`,
      })) as IPendingAuth;
      setPending(data);
      if (data.Status === 'approved') setStage('approved');
      else if (data.Status === 'denied') setStage('denied');
      else if (data.Status === 'expired') setStage('expired');
      else setStage('confirm');
    } catch (e) {
      // 404 => Code unbekannt oder abgelaufen, alles andere ist ein echter Fehler
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes('Network response was not ok')) {
        setStage('notFound');
      } else {
        setErrorMsg(msg);
        setStage('error');
      }
    }
  }, [code]);

  const decide = async (action: 'approve' | 'deny') => {
    const cleaned = code.trim().toUpperCase();
    if (!cleaned) return;
    setBusy(true);
    setErrorMsg(null);
    try {
      await customFetcher({
        endpoint: `/oauth/device/${encodeURIComponent(cleaned)}/${action}`,
        method: 'POST',
      });
      if (action === 'approve') {
        setStage('approved');
        onConnected();
      } else {
        setStage('denied');
      }
    } catch (e) {
      setErrorMsg(
        e instanceof Error
          ? e.message
          : action === 'approve'
            ? 'Verbinden fehlgeschlagen.'
            : 'Ablehnen fehlgeschlagen.'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Gerät verbinden</DialogTitle>
      <DialogContent>
        {(stage === 'input' || stage === 'notFound' || stage === 'error') && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant='body2' color='text.secondary'>
              Geben Sie den Code ein, der Ihnen in der DiNo-Mitarbeiter-App
              angezeigt wird.
            </Typography>
            {stage === 'notFound' && (
              <Alert severity='warning'>
                Dieser Code ist unbekannt oder abgelaufen. Bitte starten Sie den
                Verbindungsvorgang in der App neu.
              </Alert>
            )}
            {stage === 'error' && (
              <Alert severity='error'>
                {errorMsg || 'Es ist ein unbekannter Fehler aufgetreten.'}
              </Alert>
            )}
            <TextField
              label='Code'
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder='K7M4-9XQR'
              autoFocus
              fullWidth
              inputProps={{
                style: { letterSpacing: 2, fontFamily: 'monospace' },
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') void lookup();
              }}
            />
          </Stack>
        )}

        {stage === 'loading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {stage === 'confirm' && pending && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity='info' icon={<Smartphone />}>
              <strong>{prettyClientName(pending.ClientName)}</strong> möchte auf
              Ihr DiNo-Konto zugreifen.
            </Alert>
            <Stack direction='row' spacing={1} alignItems='center'>
              <Typography variant='caption' color='text.secondary'>
                Code
              </Typography>
              <Chip
                size='small'
                variant='outlined'
                label={pending.userCode}
                sx={{ fontFamily: 'monospace', letterSpacing: 1 }}
              />
              {pending.Expires_at && (
                <Typography variant='caption' color='text.secondary'>
                  gültig bis{' '}
                  {new Date(pending.Expires_at).toLocaleString('de-DE')}
                </Typography>
              )}
            </Stack>
            <Typography variant='body2'>
              Bitte nur bestätigen, wenn Sie diese Anfrage soeben selbst
              ausgelöst haben. Danach kann das Gerät in Ihrem Namen auf DiNo
              zugreifen, bis Sie die Sitzung hier wieder beenden.
            </Typography>
            {errorMsg && <Alert severity='error'>{errorMsg}</Alert>}
          </Stack>
        )}

        {stage === 'approved' && (
          <Alert severity='success' icon={<CheckCircle />} sx={{ mt: 1 }}>
            Gerät verbunden. Es erscheint jetzt in der Liste Ihrer Sitzungen; in
            der App können Sie fortfahren.
          </Alert>
        )}

        {stage === 'denied' && (
          <Alert severity='warning' icon={<Close />} sx={{ mt: 1 }}>
            Die Anfrage wurde abgelehnt. Das Gerät erhält keinen Zugriff.
          </Alert>
        )}

        {stage === 'expired' && (
          <Alert severity='warning' sx={{ mt: 1 }}>
            Die Anfrage ist abgelaufen. Bitte starten Sie den Verbindungsvorgang
            in der App neu.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        {stage === 'confirm' ? (
          <>
            <Button color='inherit' onClick={onClose} disabled={busy}>
              Abbrechen
            </Button>
            <Button
              color='inherit'
              startIcon={<Close />}
              onClick={() => void decide('deny')}
              disabled={busy}
            >
              Ablehnen
            </Button>
            <Button
              variant='contained'
              startIcon={<CheckCircle />}
              onClick={() => void decide('approve')}
              disabled={busy}
            >
              {busy ? 'Verbinde…' : 'Verbinden'}
            </Button>
          </>
        ) : stage === 'input' || stage === 'notFound' || stage === 'error' ? (
          <>
            <Button color='inherit' onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              variant='contained'
              onClick={() => void lookup()}
              disabled={!code.trim()}
            >
              Code prüfen
            </Button>
          </>
        ) : (
          <Button variant='contained' onClick={onClose}>
            Schließen
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
